import useBottomSheet from 'hooks/common/useBottomSheet';
import Button from 'components/common/Button';

const SHEET_ID = 'LocalPriceSheet';

interface LocalPriceData {
    title?: string;
    /** 상단 히어로 일러스트 경로. 없으면 기본 코인 일러스트 */
    imageUrl?: string;
    /** 현지 통화 포맷 가격 문자열 (예: "₹359.00") */
    price?: string;
    /** primary CTA 문구 (상점 이동, 예: "상점 보기") */
    confirmText?: string;
    /** 보조 텍스트 버튼 (예: "다음에 볼게요"), 없으면 미노출 */
    secondaryText?: string;
}

/** 기본 히어로 일러스트 — 소프트 3D 코인 스택(저채도 매트 톤) */
const DEFAULT_IMAGE = '/imgweb/sale/coinstack3d.png';

/**
 * "당신을 위한 가격"(지역 맞춤 특가) 안내 바텀시트 (refined-ui / Toss 방법론).
 * - 텍스트/가격은 vanilla(loadingmngr._showLocalPricePopup)에서 data로 넘겨받아 그대로 렌더.
 * - 액센트(버뮤다 퍼플)는 핵심 수치(가격)와 액션(CTA)에만. 그 외는 뉴트럴 토큰.
 * - 배지/보더 없이 여백으로 위계. 할인/세일 프레이밍 아님.
 */
function LocalPriceSheet() {
    const { closeWithAnimation, sheets } = useBottomSheet();
    const data = (sheets[SHEET_ID]?.data ?? {}) as LocalPriceData;

    // 닫힘 시 data로 의도를 전달 → vanilla .then(result)가 상점 이동 여부 분기
    const handleGoShop = () => {
        closeWithAnimation(SHEET_ID, { data: 'shop' });
    };

    const handleDismiss = () => {
        closeWithAnimation(SHEET_ID, { data: 'dismiss' });
    };

    return (
        <>
            <div className="flex flex-col w-full items-center rounded-t-lg bg-dialog-bg-default gap-[var(--spacing-lg)] pt-[var(--spacing-lg)]">
                {/* 히어로 일러스트 — 감정 전달용, 절제된 크기로 가격과 경쟁하지 않게 */}
                <img
                    className="w-[144px] h-[144px] object-contain"
                    alt=""
                    src={data.imageUrl || DEFAULT_IMAGE}
                />

                {/* 핵심 수치 → 헤드라인 순. 배경 박스 없이 여백으로만 위계 */}
                {/* 가격·타이틀은 한 덩어리(sm=8px), 이미지와는 lg=16px, 액션과는 32px */}
                <div className="flex flex-col w-full items-center gap-[var(--spacing-sm)]">
                    {/* 유일한 액센트(퍼플) */}
                    {data.price ? (
                        <span className="typography-title-xl text-base-txt-purple not-italic tabular-nums">
                            {data.price}
                        </span>
                    ) : null}

                    {/* 헤드라인 — 뉴트럴 */}
                    <p className="self-stretch text-center typography-title-lg not-italic text-dialog-txt-default m-0">
                        {data.title || 'A price made for you'}
                    </p>
                </div>

                {/* 액션 — primary 하나, 하단. 탭 시 마이크로 인터랙션 */}
                <div className="flex flex-col w-full items-center gap-[var(--spacing-2xs)] mt-[var(--spacing-lg)]">
                    <Button
                        className="w-full transition-transform duration-200 ease-out active:scale-[0.98] motion-reduce:transition-none motion-reduce:active:scale-100"
                        style={{ text: `${data.confirmText || 'Go to shop'}`, color: 'btn-bg-primary', fontColor: 'btn-txt-primary', borderRadius: 'lg' }}
                        onClick={handleGoShop}
                    />
                    {data.secondaryText ? (
                        <button
                            type="button"
                            className="bg-transparent border-0 py-[var(--spacing-md)] typography-body-md text-dialog-txt-secondary cursor-pointer transition-transform duration-200 ease-out active:scale-[0.98] motion-reduce:transition-none motion-reduce:active:scale-100"
                            onClick={handleDismiss}
                        >
                            {data.secondaryText}
                        </button>
                    ) : null}
                </div>
            </div>
            <div className="h-[34px]" />
        </>
    );
}

export default LocalPriceSheet;
